import i18n from '@/scripts/app-configs/i18n-config'
import { preferredLanguage } from '@/scripts/app-configs/language/languages-configs'
import getLocalFiles from '@/scripts/tools/get-local-files'
import systemStoragePlugin from '@/store/plugins/systemStorage'
import { $date } from '@/plugins'

const APP_VERSION_MAJOR = 1
const APP_VERSION_MINOR = 0

import { CONFIG } from '@/scripts/app-configs/constants'

const appVersionStruct = () => {
  return {
    date: null,
    release: null,
    commit: null,
    commitCount: null,
  }
}

const defaultSystemState = () => {
  return {
    UIReady: false,
    UILoadingMessage: 'Booting app...',
    version: {
      major: APP_VERSION_MAJOR,
      minor: APP_VERSION_MINOR,
      client: appVersionStruct(),
      build: appVersionStruct(),
    },
    language: preferredLanguage,
    measurementSystem: 'imperial',
    routeBeforeInactivity: {
      userId: null,
      routeFullPath: null,
    },
  }
}

export default {
  namespaced: true,
  state: {
    ...defaultSystemState(),
  },
  getters: {
    uiReady: (state) => state.UIReady,
    uiLoadingMessage: (state) => state.UILoadingMessage,
    appVersion: (state) => state.version.client,
    appVersionAnnotation: (state) => {
      const major = state.version.major
      const minor = state.version.minor
      const patch = state.version?.client?.commitCount || '0'
      const buildNumber = state.version?.client?.commit ? `(${state.version.client.commit.slice(0, 6)})` : ''

      return `v${major}.${minor}.${patch} ${buildNumber}`
    },
    appUpdated: (state) => {
      function formatVersionHash(versionObject) {
        return `${versionObject.commit}_${versionObject.release}`
      }
      const { build, client } = state.version

      return formatVersionHash(build) === formatVersionHash(client)
    },
    getLanguage: (state) => state.language,
    getMeasurementSystem: (state) => state.measurementSystem,
    getRouteBeforeInactivity: (state) => state.routeBeforeInactivity,
    defaultPage: (state, getters, rootState, rootGetters) => {
      const userExists = rootGetters['user/userExist']
      const userRole = rootGetters['user/getUserRole']

      if (!userExists || !userRole) {
        return { name: 'login', query: { redirected: true } }
      }

      return { name: 'feed-home', params: {} }
    },
  },
  mutations: {
    setGlobalLoadingMessage: (state, message) => {
      state.UILoadingMessage = message
    },
    resetGlobalLoadingMessage: (state) => {
      state.UILoadingMessage = defaultSystemState().UILoadingMessage
    },
    setUIReadyState: (state, status) => {
      state.UIReady = status
    },
    changeLanguage: (state, lang) => {
      i18n.locale = lang
      state.language = i18n.locale
      $date.locale(i18n.locale)
    },
    changeMeasurementSystem: (state, units) => {
      state.measurementSystem = units || (!CONFIG.environment.includes('eu') ? 'imperial' : 'international')
    },
    setAppVersion: (state, { context = 'client', versionObj = {} }) => {
      if (!state.version[context]) {
        console.error('[app-version] - Unable to set app version. Unsupported version context')

        return
      }
      Object.keys(appVersionStruct()).forEach((key) => {
        state.version[context][key] = versionObj[key]
      })
    },
    setRouteBeforeInactivity(state, { userId, routeFullPath }) {
      state.routeBeforeInactivity.userId = userId
      state.routeBeforeInactivity.routeFullPath = routeFullPath
    },
    clearRouteBeforeInactivity(state) {
      Object.assign(state.routeBeforeInactivity, defaultSystemState().routeBeforeInactivity)
    },
  },
  actions: {
    async importPortalVersion({ commit }) {
      try {
        const { data: versionObj } = await getLocalFiles('version.json')

        commit('setAppVersion', { versionObj })

        return versionObj
      } catch (error) {
        return Promise.reject(error)
      }
    },
    resetSystem({ commit }) {
      // Remove persistent copy of this store.
      systemStoragePlugin.storage.removeItem(systemStoragePlugin.key)
      const systemDefaults = defaultSystemState()

      commit('changeMeasurementSystem', systemDefaults.measurementSystem)
    },
  },
}
